import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 as uuidv4 } from 'uuid';
import { auth, db, storage } from '../../firebase';
import { collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { placeholder } from '../../photos/index.js';
import { useTranslatedText } from '../i18n/useTranslatedText.js';
import './MyResources.css';

const isStorageUrl = (value) =>
  typeof value === 'string' && (value.startsWith('https://') || value.startsWith('http://') || value.startsWith('gs://'));

const getFileName = (value) => {
  if (!value || typeof value !== 'string') return '';
  const last = value.split('/').pop();
  return last || value;
};

const getFileKind = (value) => {
  const name = typeof value === 'string' ? getFileName(value) : value?.name || '';
  const lower = name.toLowerCase();

  if (typeof value === 'object' && value && value.type) {
    if (value.type.startsWith('image/')) return 'image';
    if (value.type.startsWith('video/')) return 'video';
    if (value.type.startsWith('audio/')) return 'audio';
    if (value.type === 'application/pdf') return 'pdf';
  }

  if (lower.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) return 'image';
  if (/\.(mp4|webm|ogg|mov)$/.test(lower)) return 'video';
  if (/\.(mp3|wav|ogg|m4a)$/.test(lower)) return 'audio';
  return 'file';
};

const toFullPath = (value) => {
  if (!value || typeof value !== 'string') return value;
  try {
    return ref(storage, value).fullPath;
  } catch {
    return value;
  }
};

const MediaItem = ({ file, index, moveMedia, removeMedia, fallbackFileText, removeFileLabel }) => {
  const [, dragRef] = useDrag({
    type: 'media',
    item: { index },
  });

  const [, dropRef] = useDrop({
    accept: 'media',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveMedia(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const kind = useMemo(() => getFileKind(file), [file]);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    const loadPreview = async () => {
      try {
        if (file instanceof File) {
          objectUrl = URL.createObjectURL(file);
          setPreviewUrl(objectUrl);
          return;
        }

        if (typeof file === 'string') {
          if (isStorageUrl(file)) {
            setPreviewUrl(file);
            return;
          }

          const url = await getDownloadURL(ref(storage, file));
          if (!cancelled) setPreviewUrl(url);
          return;
        }

        setPreviewUrl(null);
      } catch (error) {
        console.error('Error loading preview:', error);
        if (!cancelled) setPreviewUrl(null);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  const fileName = file instanceof File ? file.name : getFileName(file);

  return (
    <div ref={(node) => dragRef(dropRef(node))} className="media-items">
      {kind === 'image' && previewUrl && <img src={previewUrl} alt={fileName ? `Preview ${fileName}` : 'Preview'} className="preview-media" />}
      {kind === 'video' && previewUrl && <video src={previewUrl} className="preview-media" controls muted />}
      {kind === 'audio' && previewUrl && <audio src={previewUrl} className="preview-media" controls />}
      {kind === 'pdf' && previewUrl && (
        <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="preview-media">
          {fileName || 'PDF'}
        </a>
      )}
      {kind === 'file' && <div className="preview-media">{fileName || fallbackFileText}</div>}

      <button type="button" className="remove-media-button" onClick={() => removeMedia(index)} aria-label={removeFileLabel}>
        ×
      </button>
    </div>
  );
};

const MyResources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingResource, setEditingResource] = useState(null);
  const [editingMedia, setEditingMedia] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const loadingText = useTranslatedText('Yükleniyor...');
  const myResourcesTitle = useTranslatedText('Kaynaklarım');
  const noResourcesText = useTranslatedText('Hiç kaynak bulunamadı.');
  const authorLabel = useTranslatedText('Yazar:');
  const publishDateLabel = useTranslatedText('Yayın Tarihi:');
  const statusLabel = useTranslatedText('Durum:');
  const viewText = useTranslatedText('Görüntüle');
  const editText = useTranslatedText('Düzenle');
  const deleteText = useTranslatedText('Sil');
  const editResourceTitle = useTranslatedText('Kaynak Düzenle');
  const uploadFileLabel = useTranslatedText('Dosya Yükle:');
  const resourceTitleLabel = useTranslatedText('Kaynak Başlığı:');
  const descriptionLabel = useTranslatedText('Açıklama:');
  const saveChangesText = useTranslatedText('Değişiklikleri Kaydet');
  const deleteConfirmTitle = useTranslatedText("Bu Kaynağı Silmek İstediğinize Emin Misiniz?");
  const confirmDeleteText = useTranslatedText('Evet, Sil');
  const cancelText = useTranslatedText('İptal');
  const removeFileText = useTranslatedText('Dosyayı kaldır');
  const fileFallbackText = useTranslatedText('Dosya');
  const notFoundErrorText = useTranslatedText('Kaynak bulunamadı');
  const genericErrorText = useTranslatedText('Bir hata oluştu. Lütfen tekrar deneyin.');
  const updateSuccessText = useTranslatedText('Kaynak güncellendi!');
  const updateFailedText = useTranslatedText('Kaynak güncelleme başarısız. Lütfen tekrar deneyin.');

  useEffect(() => {
    const fetchResources = async () => {
      const user = auth.currentUser;
      if (user) {
        const q = query(collection(db, 'resources'), where('createdBy', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const resourceList = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setResources(resourceList);
      }
      setLoading(false);
    };

    fetchResources();
  }, []);

  const handleDelete = (resourceId) => {
    setDeleteConfirmation(resourceId);
  };

  const confirmDelete = async () => {
    try {
      const resourceDoc = await getDoc(doc(db, 'resources', deleteConfirmation));
      if (!resourceDoc.exists()) throw new Error(notFoundErrorText);
      const resourceData = resourceDoc.data();

      const filesToDelete = Array.isArray(resourceData.files)
        ? resourceData.files
        : Array.isArray(resourceData.mediaUrls)
          ? resourceData.mediaUrls
          : [];

      await Promise.all(
        filesToDelete.map(async (pathOrUrl) => {
          if (typeof pathOrUrl !== 'string' || pathOrUrl.length === 0) return;
          try {
            await deleteObject(ref(storage, pathOrUrl));
          } catch (error) {
            console.error('Error deleting file:', error);
          }
        })
      );

      if (resourceData.thumbnailUrl) {
        try {
          await deleteObject(ref(storage, resourceData.thumbnailUrl));
        } catch (error) {
          console.error('Error deleting thumbnail:', error);
        }
      }

      await deleteDoc(doc(db, 'resources', deleteConfirmation));
      setResources(resources.filter((res) => res.id !== deleteConfirmation));
      setDeleteConfirmation(null);
    } catch (error) {
      console.error('Error during resource deletion:', error);
      alert(genericErrorText);
    }
  };

  const handleEdit = (resource) => {
    const normalized = (Array.isArray(resource.files) ? resource.files : Array.isArray(resource.mediaUrls) ? resource.mediaUrls : [])
      .map(toFullPath)
      .filter(Boolean);

    setEditingResource({
      id: resource.id,
      title: resource.title || '',
      author: resource.author || '',
      publishDate: resource.publishDate || '',
      description: resource.description || '',
      files: normalized,
    });

    setEditingMedia(normalized);
  };

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    setEditingMedia((prev) => [...prev, ...files]);
  };

  const handleRemoveMedia = (index) => {
    setEditingMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReorderMedia = (fromIndex, toIndex) => {
    setEditingMedia((prev) => {
      const updated = [...prev];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      return updated;
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const resourceId = editingResource.id;
      const sanitizedTitle = (editingResource.title || '').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const resourceDirectory = `resources/${resourceId}_${sanitizedTitle}`;

      const prevFiles = Array.isArray(editingResource.files) ? editingResource.files.map(toFullPath).filter(Boolean) : [];

      const nextFiles = [];
      for (const item of editingMedia) {
        if (item instanceof File) {
          const uniqueFileName = `${uuidv4()}_${item.name}`;
          const fileRef = ref(storage, `${resourceDirectory}/${uniqueFileName}`);
          await uploadBytes(fileRef, item, {
            contentType: item.type || 'application/octet-stream',
          });
          nextFiles.push(fileRef.fullPath);
        } else if (typeof item === 'string') {
          nextFiles.push(toFullPath(item));
        }
      }

      const keptExisting = new Set(nextFiles.filter((v) => typeof v === 'string'));
      const removedFiles = prevFiles.filter((v) => typeof v === 'string' && !keptExisting.has(v));

      await Promise.all(
        removedFiles.map(async (fullPath) => {
          try {
            await deleteObject(ref(storage, fullPath));
          } catch (error) {
            console.error('Error deleting removed file:', error);
          }
        })
      );

      const updatePayload = {
        title: editingResource.title,
        author: editingResource.author,
        publishDate: editingResource.publishDate,
        description: editingResource.description,
        files: nextFiles,
      };

      await updateDoc(doc(db, 'resources', resourceId), updatePayload);

      setResources((prev) => prev.map((res) => (res.id === resourceId ? { ...res, ...updatePayload } : res)));
      setEditingResource(null);
      setEditingMedia([]);
      alert(updateSuccessText);
    } catch (error) {
      console.error('Error updating resource:', error);
      alert(updateFailedText);
    }
  };

  if (loading) {
    return <div>{loadingText}</div>;
  }

  return (
    <div className="my-resources">
      <h2>{myResourcesTitle}</h2>
      {resources.length === 0 ? (
        <p>{noResourcesText}</p>
      ) : (
        resources.map((resource) => (
          <div key={resource.id} className="resource-item">
            <div className="resource-thumbnail">
              <img src={resource.thumbnailUrl || placeholder} alt={resource.title} />
            </div>
            <div className="resource-content">
              <h3>{resource.title}</h3>
              <div className="resource-info">
                <p>
                  <span>{authorLabel}</span> {resource.author}
                </p>
                <p>
                  <span>{publishDateLabel}</span> {resource.publishDate}
                </p>
                <p>
                  <span>{statusLabel}</span> {resource.status}
                </p>
              </div>
              <div className="resource-actions">
                <Link to={`/resource/${resource.id}`} className="btn btn-view">
                  {viewText}
                </Link>
                <button className="btn btn-edit" onClick={() => handleEdit(resource)}>
                  {editText}
                </button>
                <button className="btn btn-delete" onClick={() => handleDelete(resource.id)}>
                  {deleteText}
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {editingResource && (
        <div className="edit-resource-overlay">
          <div className="edit-resource-form">
            <div className="edit-form-header">
              <h2>{editResourceTitle}</h2>
              <button className="close-button" onClick={() => setEditingResource(null)}>
                ×
              </button>
            </div>

            <div className="form-group">
              <label className="media-upload-label">{uploadFileLabel}</label>
              <input
                type="file"
                multiple
                onChange={handleMediaChange}
                className="media-upload-input"
                accept=".pdf, image/*, audio/*, video/*"
              />
              <DndProvider backend={HTML5Backend}>
                <div className="media-previews">
                  {editingMedia.map((media, index) => (
                    <MediaItem
                      key={index}
                      file={media}
                      index={index}
                      moveMedia={handleReorderMedia}
                      removeMedia={handleRemoveMedia}
                      fallbackFileText={fileFallbackText}
                      removeFileLabel={removeFileText}
                    />
                  ))}
                </div>
              </DndProvider>
            </div>

            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label>{resourceTitleLabel}</label>
                <input
                  type="text"
                  value={editingResource.title}
                  onChange={(e) => setEditingResource({ ...editingResource, title: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{authorLabel}</label>
                <input
                  type="text"
                  value={editingResource.author}
                  onChange={(e) => setEditingResource({ ...editingResource, author: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{publishDateLabel}</label>
                <input
                  type="date"
                  value={editingResource.publishDate}
                  onChange={(e) => setEditingResource({ ...editingResource, publishDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{descriptionLabel}</label>
                <textarea
                  value={editingResource.description}
                  onChange={(e) => setEditingResource({ ...editingResource, description: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-save">
                  {saveChangesText}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirmation && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation">
            <h2>{deleteConfirmTitle}</h2>
            <div className="btn-group">
              <button className="btn-confirm" onClick={confirmDelete}>
                {confirmDeleteText}
              </button>
              <button className="btn-cancel" onClick={() => setDeleteConfirmation(null)}>
                {cancelText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyResources;
